import React, { useEffect, useState } from 'react';

import Formsy from 'formsy-react';
import { Plus, Trash } from 'lucide-react';
import { FormBody, Row, Col, Button, Label, FormGroup, Tabs, TabsList, TabsTrigger, TabsContent, Card, FormCard, FormFooter, DefaultTooltip, Select as TWSelect, SimpleAlert } from 'twyne-new.ui';

import CodeEditor from '@Components/CodeEditor';
import { buttonLabels } from '@Components/rules/options';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';
import Rules from 'components/rules/Rules';
import history from 'utils/history';

import useExtraFields from './useExtraFields';

const LOCATION_OPTIONS = [
  { value: 'head', label: 'Head' },
  { value: 'body', label: 'Body' },
  { value: 'footer', label: 'Footer' },
]

const TYPE_OPTIONS = [
  { value: 'html', label: 'HTML' },
  { value: 'javascript', label: 'Javascript' },
]

const DEFAULT_SNIPPET = {
  type: 'html',
  content: '',
}

const CONTENT_TYPE_KEY = 'cmsContentType'

const defaultRules = () => ({
  condition: 'and',
  groups: [{
    condition: 'and',
    rules: [{ key: CONTENT_TYPE_KEY, type: 'cms-page', equality: 'included' }],
  }],
})

export default class SnippetForm extends React.Component {
  constructor(props) {
    super(props)
    const data = props.data || {}

    this.state = {
      name: '',
      location: '',
      active: true,
      ...data,
      rules: data.rules || defaultRules(),
      content: this.parseContent(data.content),
      conflictEvent: null,
      contentError: null,
    }
  }

  parseContent(data) {
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data)
      } catch {
        data = []
      }
    }

    if (!Array.isArray(data) || data.length === 0) {
      return [{ ...DEFAULT_SNIPPET }]
    }

    return data
  }

  handleConflict = event => {
    this.setState({ conflictEvent: event })
  }

  validateContent = () => {
    const { content } = this.state

    if (!Array.isArray(content) || content.length === 0) {
      return 'At least one content block is required.'
    }
    if (content.some(block => !block.type || !block.content?.trim())) {
      return 'Every content block must have a type and content.'
    }

    return null
  }

  hasContentType = () => {
    const { rules } = this.state

    return !!rules?.groups?.some(group => group.rules?.some(rule => rule.key === CONTENT_TYPE_KEY && `${rule.value ?? ''}`.trim()))
  }

  handleValidSubmit = model => {
    const contentError = this.validateContent()
    if (contentError) {
      this.setState({ contentError })
      return
    }

    if (!this.hasContentType()) {
      return
    }

    const payload = { ...this.state, ...model, content: this.state.content }
    delete payload.conflictEvent
    delete payload.contentError

    this.props.handleSubmit(payload)
  }

  ContentBlocks = () => {
    const [blocks, setBlocks] = useState(this.state.content?.length ? this.state.content : [{ ...DEFAULT_SNIPPET }])
    const [activeBlock, setActiveBlock] = useState('block-0')

    useEffect(() => {
      this.setState({ content: blocks, contentError: null })
    }, [blocks])

    const handleAddBlock = () => {
      setActiveBlock(`block-${blocks.length}`)
      setBlocks(current => [...current, { ...DEFAULT_SNIPPET }])
    }

    const handleRemoveBlock = position => {
      if (blocks.length <= 1) {
        return
      }
      setActiveBlock('block-0')
      setBlocks(current => current.filter((_, index) => index !== position))
    }

    const handleChangeType = (position, type) => {
      setBlocks(current => current.map((block, index) => index === position ? { ...block, type } : block))
    }

    const handleChangeContent = (position, value) => {
      setBlocks(current => current.map((block, index) => index === position ? { ...block, content: value ?? '' } : block))
    }

    return <Col size='12'>
      <Row>
        <Col size='12'>
          <div className='float-right'>
            <Button color='accent' size='sm' onClick={handleAddBlock}>
              <Plus size={14} />
              Add
            </Button>
          </div>
        </Col>
        <Col size='12'>
          <Tabs value={activeBlock} onValueChange={setActiveBlock} forceMount>
            <TabsList>
              {blocks.map((block, index) => <TabsTrigger className='pt-0' key={index} value={`block-${index}`}>
                Snippet {index + 1}
              </TabsTrigger>)}
            </TabsList>
            {blocks.map((block, index) => <TabsContent key={index} value={`block-${index}`}>
              <Card className='pb-2'>
                <Row>
                  <Col size='12'>
                    <FormGroup>
                      <Label>Type</Label>
                      <TWSelect
                        options={TYPE_OPTIONS}
                        value={block.type}
                        clearable={false}
                        onChange={option => handleChangeType(index, option.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col size='12'>
                    <FormGroup>
                      <Label>Content</Label>
                      <CodeEditor
                        language={block.type}
                        value={block.content || ''}
                        onChange={value => handleChangeContent(index, value)}
                        height='300px'
                        width='100%'
                      />
                    </FormGroup>
                  </Col>
                  <Col size='12'>
                    <div className='flex justify-end'>
                      <Button
                        onClick={() => handleRemoveBlock(index)}
                        variant='outline'
                        color='danger'
                        size='sm'
                        type='button'
                        disabled={blocks.length <= 1}
                      >
                        <Trash size={14} />
                        Remove
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card>
            </TabsContent>)}
          </Tabs>
        </Col>
      </Row>
    </Col>
  }

  RulesCard = () => {
    const { rules } = this.state
    const extraFields = useExtraFields()

    return <FormCard title='Rules'>
      {!this.hasContentType() && <SimpleAlert className='mb-2' variant='warning'>
        Add at least one Content Type rule with a value to save the snippet.
      </SimpleAlert>}
      <Rules
        origin='cms-snippet'
        rules={rules}
        extraFields={extraFields}
        handleChange={state => this.setState(state)}
        onConflict={this.handleConflict}
      />
    </FormCard>
  }

  render() {
    const { conflictEvent, contentError } = this.state
    const missingContentType = !this.hasContentType()
    const disableForm = missingContentType || conflictEvent === 'conflict' || conflictEvent === 'checking'
    const disableMessage = missingContentType ? "You can't save the snippet without a Content Type rule!" : "You can't save the snippet with rules conflict!"

    return <Formsy onValidSubmit={this.handleValidSubmit}>
      <FormBody>
        <Row>
          <Col size='12'>
            <FormCard title='General'>
              <Row>
                <Col size='6'>
                  <FormGroup>
                    <Label>Name</Label>
                    <Input name='name' value={this.state.name || ''} required />
                  </FormGroup>
                </Col>
                <Col size='4'>
                  <FormGroup>
                    <Label>Location</Label>
                    <Select name='location' value={this.state.location} options={LOCATION_OPTIONS} required />
                  </FormGroup>
                </Col>
                <Col size='2'>
                  <FormGroup>
                    <Label>Active</Label>
                    <Switch name='active' value={this.state.active ?? true} />
                  </FormGroup>
                </Col>
              </Row>
            </FormCard>
          </Col>
          <Col size='12'>
            <FormCard title='Content'>
              <Row>
                <this.ContentBlocks />
                {contentError && <Col size='12'>
                  <SimpleAlert className='mt-2' variant='danger'>{contentError}</SimpleAlert>
                </Col>}
              </Row>
            </FormCard>
          </Col>
          <Col size='12'>
            <this.RulesCard />
          </Col>
        </Row>
      </FormBody>
      <FormFooter>
        <DefaultTooltip side='top' content={disableForm && disableMessage}>
          <Button type='submit' disabled={disableForm} color='accent'>{buttonLabels[conflictEvent] || 'Save'}</Button>
        </DefaultTooltip>
        <Button onClick={() => history.goBack()} variant='ghost'>Cancel</Button>
      </FormFooter>
    </Formsy>
  }
}
